import React, {useState, ChangeEvent, useEffect} from 'react';
import {I18n} from 'react-redux-i18n';
import {useDispatch} from 'react-redux';

import {Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  withStyles,
  WithStyles} from '@material-ui/core';

import {CustomButton, CustomInput} from '../../material-dashboard-pro-react/components';
import {Autocomplete} from '../../components';

import {AutoCompleteOption} from '../../types/Autocomplete';
import {getCategoriesList, sendDomainReport} from './action';

import {style} from './style';

const MAX_COMMENT_LENGTH = 300;

const COMMENT_ROWS = 5;

type ReportDomainModalProps = WithStyles<typeof style> & {
  domain: string;
  domainCategories: string[];
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setIsOpen: (open: boolean) => void;
};

function ReportDomainModal({
  classes,
  domain,
  domainCategories,
  isOpen,
  setIsOpen,
}: ReportDomainModalProps) {
  const dispatch = useDispatch();

  const [categoriesList, setCategoriesList] = useState<AutoCompleteOption[]>([]);
  const [isReclassify, setIsReclassify] = useState(false);
  const [isMalicious, setIsMalicious] = useState(false);
  const [selectedSuggestedCategory, setSelectedSuggestedCategory]
    = useState<AutoCompleteOption | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  useEffect(() => {
    setIsReclassify(false);
    setIsMalicious(false);
    setSelectedSuggestedCategory(null);
    setComment('');
  }, [isOpen]);

  useEffect(() => {
    if (!comment) {
      setCommentError(I18n.t('lookupTool.reportDomain.validation.commentRequired'));
    } else if (comment.length > MAX_COMMENT_LENGTH) {
      setCommentError(
        I18n.t('lookupTool.reportDomain.validation.commentTooLong', {max: MAX_COMMENT_LENGTH}),
      );
    } else {
      setCommentError('');
    }
  }, [comment]);

  useEffect(() => {
    if (categoriesList.length === 0) {
      getCategoriesList(
        (categories) => setCategoriesList(categories.map((option, index) => (
          {
            label: option,
            value: option,
            index,
          }
        ))),
      )(dispatch);
    }
  }, []);

  return (
    <Dialog
      classes={{paper: classes.modal}}
      fullWidth={true}
      maxWidth="sm"
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <div className={classes.modal__header}>
        <h2 className={classes.header__title}>{I18n.t('lookupTool.reportDomain.title', {domain})}</h2>
        <span className={classes.header__categories}>{domainCategories.join(', ')}</span>
      </div>

      <DialogContent classes={{root: classes.modal}}>
        <FormControl component="fieldset">
          <div className={classes.modal__checkboxesGroup}>
            <FormControlLabel
              value={isReclassify}
              control={<Checkbox />}
              label={I18n.t('lookupTool.reportDomain.reclassify')}
              data-testid="lookup-report-reason-reclassify"
              onChange={(_, checked: boolean) => setIsReclassify(checked)}
            />
            <FormControlLabel
              value={isMalicious}
              control={<Checkbox />}
              label={I18n.t('lookupTool.reportDomain.reportMalicious')}
              data-testid="lookup-report-reason-malicious"
              onChange={(_, checked: boolean) => setIsMalicious(checked)}
            />
          </div>
        </FormControl>

        <div className={classes.modal__suggestCategory}>
          <InputLabel className={classes.modal__label} htmlFor="suggested-category">
            {I18n.t('lookupTool.reportDomain.suggestedCategory')}
          </InputLabel>
          <Autocomplete
            closeMenuOnSelect={true}
            isDisabled={!isReclassify}
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              'placeholder': I18n.t('lookupTool.reportDomain.search'),
              'id': 'suggested-category',
              'name': 'suggested-category',
              'isMulti': false,
              'onChange': (selectedOption: AutoCompleteOption) => {
                setSelectedSuggestedCategory(selectedOption);
              },
              'options': categoriesList,
              'value': selectedSuggestedCategory,
              'data-testid': 'lookup-report-suggested-category-dropdown',
            }}
            isCreatable={false}
          />
        </div>

        <CustomInput
          errorText={commentError}
          error={!!commentError}
          shrink={true}
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            placeholder: I18n.t('lookupTool.reportDomain.commentPlaceholder'),
            multiline: true,
            rows: COMMENT_ROWS,
            value: comment,
            name: 'allowed',
            onChange: ((e: ChangeEvent<HTMLInputElement>) => setComment(e.target.value)),
            type: 'text',
          }}
          labelText={I18n.t('lookupTool.reportDomain.comment')}
          data-testid="lookup-report-comment"
        />

      </DialogContent>

      <DialogActions className={classes.modal__actions}>
        <CustomButton
          disabled={!!commentError || (!isReclassify && !isMalicious)}
          color="secondary"
          onClick={() => {
            sendDomainReport({
              fqdn: domain,
              reclassify: isReclassify,
              malicious: isMalicious,
              suggestedCategory: selectedSuggestedCategory?.label ?? '',
              comment: comment,
            })(dispatch);
            setIsOpen(false);
          }}
        >
          {I18n.t('lookupTool.reportDomain.submit')}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}

export default withStyles(style)(ReportDomainModal);
